import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Search } from "lucide-react";
import { useProductRatings } from "@/hooks/useProductRatings";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryRail from "@/components/CategoryRail";
import { Seo } from "@/context/SeoContext";

export default function BoutiquePage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(searchParams.get("categorie") || "tous");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [topFirst, setTopFirst] = useState(false);
  const ratings = useProductRatings();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const list = products.filter((p) => {
      const matchCategory = category === "tous" || p.category === category;
      const matchSearch = p.name.toLowerCase().includes(search.trim().toLowerCase());
      return matchCategory && matchSearch;
    });
    if (!topFirst) return list;
    const score = (p) => ratings[p.product_id] || { avg: 0, count: 0 };
    return [...list].sort((a, b) => score(b).avg - score(a).avg || score(b).count - score(a).count);
  }, [products, category, search, topFirst, ratings]);
  const hasRatings = Object.keys(ratings).length > 0;

  const activeCategory = categories.find((c) => c.category_id === category);

  const counts = useMemo(() => {
    const map = {};
    for (const p of products) map[p.category] = (map[p.category] || 0) + 1;
    return map;
  }, [products]);

  return (
    <div data-testid="boutique-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <Seo pageKey="boutique" />
      <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow">La Boutique</p>
          <h1 className="mt-2 font-serif text-4xl font-semibold tracking-tight text-bark sm:text-5xl">
            {activeCategory ? activeCategory.name : "Toute la récolte"}
          </h1>
          <p className="mt-3 max-w-xl text-base text-bark-soft">
            {activeCategory?.description ||
              "Miels de terroir, cire, propolis et coffrets : tout est récolté et préparé à la main, en Isère."}
          </p>
        </div>
        <div className="relative w-full shrink-0 sm:w-72">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-mute" />
          <input
            data-testid="product-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Chercher une douceur…"
            className="w-full rounded-full border border-amber-900/20 bg-white py-2.5 pl-10 pr-4 text-sm text-bark outline-none transition-colors placeholder:text-stone-mute focus:border-amber-deep"
          />
        </div>
      </div>

      <CategoryRail
        categories={categories}
        counts={counts}
        active={category}
        onSelect={setCategory}
        total={products.length}
      />

      {hasRatings && (
        <div className="mb-6 flex justify-end">
          <button data-testid="sort-top-rated" onClick={() => setTopFirst((v) => !v)} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${topFirst ? "bg-amber-deep text-white" : "border border-amber-900/20 bg-white text-bark hover:border-amber-deep"}`}>
            <Award size={14} /> Les mieux notés
          </button>
        </div>
      )}

      {loading ? (
        <p data-testid="boutique-loading" className="py-20 text-center font-serif text-xl text-bark-soft">La ruche s'active…</p>
      ) : filtered.length === 0 ? (
        <p data-testid="boutique-empty" className="py-20 text-center font-serif text-xl text-bark-soft">
          Aucune douceur ne correspond à votre recherche.
        </p>
      ) : (
        <div data-testid="product-grid" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p, i) => (
            <motion.div
              key={p.product_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <ProductCard product={p} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
